import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import Logo from "@/components/Logo";

const links = [
  { href: "/speisekarte", label: "Speisekarte", isRoute: true },
  { href: "/#ueber-uns", label: "Über uns" },
  { href: "/#bewertungen", label: "Bewertungen" },
  { href: "/#bewerben", label: "Bewerben" },
  { href: "/#kontakt", label: "Kontakt" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // On non-home pages, the navbar should already look "scrolled"
  const isHome = location.pathname === "/";
  const showScrolled = scrolled || !isHome;

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
          showScrolled
            ? "bg-[#0F0F0F]/80 backdrop-blur-xl border-b border-white/5 py-3"
            : "bg-transparent py-6"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between gap-6">
          <Link to="/" className="shrink-0">
            <Logo tone="light" />
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-9">
            {links.map((l) => (
              <NavLink key={l.href} {...l} />
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4 xl:gap-5 shrink-0">
            <a
              href="tel:+492327995454"
              data-testid="nav-phone"
              className="hidden xl:flex items-center gap-2 text-sm text-white/85 hover:text-[var(--niko-gold)] transition-colors whitespace-nowrap"
            >
              <Phone className="w-4 h-4 shrink-0" />
              <span className="tracking-wide">+49 2327 995454</span>
            </a>
            <Link
              to="/speisekarte"
              data-testid="nav-menu-cta"
              className="pulse-gold inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[var(--niko-gold)] text-[var(--niko-dark)] text-[12px] font-semibold tracking-[0.16em] uppercase hover:bg-[var(--niko-gold-soft)] transition-colors whitespace-nowrap"
            >
              Speisekarte →
            </Link>
          </div>

          <button
            onClick={() => setOpen(true)}
            data-testid="nav-hamburger"
            className="lg:hidden text-white/90 p-2"
            aria-label="Menü öffnen"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </header>

      {/* Mobile menu */}
      <div
        data-testid="mobile-menu"
        className={`fixed inset-0 z-50 lg:hidden transition-all duration-500 ${
          open ? "pointer-events-auto" : "pointer-events-none"
        }`}
      >
        <div
          onClick={() => setOpen(false)}
          className={`absolute inset-0 bg-[#0F0F0F]/80 backdrop-blur-md transition-opacity duration-500 ${
            open ? "opacity-100" : "opacity-0"
          }`}
        />
        <aside
          className={`absolute top-0 left-0 right-0 bg-[#0F0F0F] border-b border-white/10 px-6 py-6 transition-transform duration-500 ${
            open ? "translate-y-0" : "-translate-y-full"
          }`}
        >
          <div className="flex items-center justify-between mb-10">
            <Logo tone="light" />
            <button
              onClick={() => setOpen(false)}
              data-testid="mobile-menu-close"
              className="p-2 text-white/80"
              aria-label="Menü schließen"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <nav className="flex flex-col gap-5">
            {links.map((l) =>
              l.isRoute ? (
                <Link
                  key={l.href}
                  to={l.href}
                  onClick={() => setOpen(false)}
                  className="font-display text-3xl text-white"
                  data-testid={`mobile-nav-${l.label}`}
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-display text-3xl text-white"
                  data-testid={`mobile-nav-${l.label}`}
                >
                  {l.label}
                </a>
              )
            )}
          </nav>
          <div className="mt-10 pt-8 border-t border-white/10 flex flex-col gap-4">
            <a
              href="tel:+492327995454"
              className="flex items-center gap-3 text-white/80"
            >
              <Phone className="w-4 h-4 text-[var(--niko-gold)]" />
              +49 2327 995454
            </a>
            <Link
              to="/speisekarte"
              onClick={() => setOpen(false)}
              className="block text-center px-5 py-4 rounded-full bg-[var(--niko-gold)] text-[var(--niko-dark)] text-sm font-semibold tracking-[0.16em] uppercase"
            >
              Speisekarte →
            </Link>
          </div>
        </aside>
      </div>
    </>
  );
}

function NavLink({ href, label, isRoute }) {
  const cls =
    "nav-link text-[13px] tracking-[0.18em] uppercase text-white/85 hover:text-white";
  if (isRoute) {
    return (
      <Link to={href} data-testid={`nav-${label}`} className={cls}>
        {label}
      </Link>
    );
  }
  return (
    <a href={href} data-testid={`nav-${label}`} className={cls}>
      {label}
    </a>
  );
}
